import {
  Box,
  Button,
  Center,
  HStack,
  ScaleFade,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  VStack,
  Flex,
  Switch,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { CQReactTable, TextInput } from '@laxmimanogna/code-quick-components';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppColors from '../../../../constants/AppColors';
import { CUSTOM } from '../../../../constants/constants';
import ROUTES from '../../../../constants/Routes';
import { FONT_FAMILY } from '../../../../constants/Theme';
import hospitalDashboardRepository from '../../../../repositories/HospitalRepository';
import { dateFormat } from '../../../../utils/common.utils';
import {
  defaultReactTableProp,
  paginationProps,
} from '../../../../utils/my_audits.utils';
import { priceConverter } from '../../../../utils/priceConverter';
import './TableCss.css';
import { format } from 'date-fns';
import InfoTooltip from '../../../../components/InfoTooltip';
import { replaceRoute } from '../../../../utils/common.utils';
import usePaginatedTable from '../../../../hooks/usePaginatedTable';
import UploadBreakdownTable from '../../components/UploadBreakdownTable';

function HAuditChartAccuracy({
  defaultStartDate,
  defaultEndDate,
  startDate,
  endDate,
  filter,
}) {
  const { hospitalId } = useParams();
  const [searchText, setSearchText] = useState('');
  const tableRef = React.useRef(null);

  const getChartAccuracyData = useCallback(
    async params => {
      const filterParams = { ...params };
      filterParams.filter = filter.toLowerCase();
      // Remove undefined values
      if (filterParams.search === undefined) delete filterParams.search;
      if (filterParams.ordering === undefined) delete filterParams.ordering;
      if (filter === CUSTOM.toLowerCase()) {
        const selectedDates = JSON.parse(
          window.localStorage.getItem('customDateFilter')
        );
        for (const prop in selectedDates) {
          filterParams[prop] = dateFormat(
            new Date(selectedDates[prop]),
            'yyyy-MM-dd'
          );
        }
        delete filterParams.filter;
      }
      filterParams.entity_id = hospitalId;

      filterParams['entity_type'] = 'hospital';

      return await hospitalDashboardRepository.getChartAccuracyData(
        filterParams
      );
    },
    [hospitalId, filter, defaultStartDate, defaultEndDate, startDate, endDate]
  );

  const {
    data: chartAccuracydata,
    isLoading: isFetching,
    error,
    pagination,
    onPageChangeByNumber,
    onSizeChange,
    onPreviousPage,
    onNextPage,
    setSort,
    setSearchQuery,
    refetch,
    setAdditionalFilters,
  } = usePaginatedTable(getChartAccuracyData);

  useEffect(() => {
    const filterParams = {};
    filterParams.filter = filter.toLowerCase();

    if (filter === CUSTOM.toLowerCase()) {
      const selectedDates = JSON.parse(
        window.localStorage.getItem('customDateFilter')
      );
      for (const prop in selectedDates) {
        filterParams[prop] = dateFormat(
          new Date(selectedDates[prop]),
          'yyyy-MM-dd'
        );
      }
      delete filterParams.filter;
    }
    setAdditionalFilters(filterParams);
    // eslint-disable-next-line
  }, [defaultStartDate, defaultEndDate, filter, startDate, endDate]);

  const handleSearch = () => {
    setSearchQuery(searchText);
  };

  const renderLoader = () => {
    return (
      <Stack p={4}>
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
      </Stack>
    );
  };

  const renderContent = () => {
    
    return (
      <ScaleFade initialScale={0.5} in={true}>
        <UploadBreakdownTable
          data={chartAccuracydata}
          isLoading={isFetching}
          pagination={pagination}
          onPageChangeByNumber={onPageChangeByNumber}
          onSizeChange={onSizeChange}
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage}
          onSort={setSort}
          refetch={refetch}
          tableRef={tableRef}
        />
      </ScaleFade>
    );
  };

  const renderFailedPage = () => {
    return (
      <VStack justifyContent={'center'} h={'100%'} textAlign={'center'}>
        <Text>Something went wrong, Please try again later</Text>
      </VStack>
    );
  };

  const renderMainContent = () => {
    if (isFetching && !chartAccuracydata?.length) {
      return renderLoader();
    } else if (error) {
      return renderFailedPage();
    } else {
      return renderContent();
    }
  };
  return (
    <Box
      marginTop={3}
      width={'100%'}
      bg={AppColors.white}
      padding={4}
      borderRadius={'lg'}
      shadow={'md'}
    >
      <HStack width={'100%'} justifyContent={'space-between'} mb={3}>
        <HStack>
          <Text fontFamily={FONT_FAMILY.ptSans} color={AppColors.primary}>
            Upload Breakdown
          </Text>
          <InfoTooltip
            infoContent={
              <>
                <p>
                  <b>Upload Breakdown</b> details each batch of charts uploaded,
                  sortable by each column header. It can also function as a work
                  queue by using the checkboxes in the far-right column,
                  allowing you to track and manage chart uploads effectively.
                </p>
              </>
            }
          />
        </HStack>
        <Box>
          <TextInput
            borderRadius={'md'}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={e => {
              if (e.keyCode === 13) {
                handleSearch();
              }
            }}
            mb="2"
            rightIconProps={{ zIndex: 0 }}
            placeholder="Search"
            rightIcon={
              <SearchIcon
                onClick={handleSearch}
                color={AppColors.gray}
                cursor="pointer"
              />
            }
          />
        </Box>
      </HStack>

      {renderMainContent()}
    </Box>
  );
}

export default HAuditChartAccuracy;
